/**
 * IAsyncLoadable - interface and helpers for async resource loading with progress tracking.
 */
export const IAsyncLoadableTag = Symbol('IAsyncLoadable');


/**
 * An async task helper that's responsible of doing an async task
 * and tracking its loading state.
 */
export class AsyncTaskWorker {
  private _promise: Promise<void> | null = null;
  private _runningTask: (() => Promise<void>) | null = null;

  /**
   * Start or restart the load process.
   * Accepts a task that returns void / stores result elsewhere.
   *
   * @param task - The async task to execute
   */
  public runTask(task: () => Promise<void>): Promise<void> {
    const currentTask = task;
    this._runningTask = currentTask;

    this._promise = (async () => {
      await task();
      // mark as done if still the same task
      if (this._runningTask === currentTask) this._promise = null;
    })();

    return this._promise;
  }

  public isRunning(): boolean {
    return this._promise !== null;
  }

  /** Waits for the current loading operation to complete. */
  public async waitUntilComplete(): Promise<void> {
    if (this._promise) await this._promise;
  }
}


/**
 * Interface for components or actors that require asynchronous loading.
 * Implement this interface to provide a standardized way to wait for loading to complete.
 */
export interface IAsyncLoadable {
  /**
   * Marker to identify objects that implement IAsyncLoadable
   */
  readonly [IAsyncLoadableTag]: true;

  /**
   * Waits for the current loading operation to complete.
   *
   * @returns Promise that resolves when loading is complete
   */
  waitForLoad(): Promise<void>;

  /**
   * Checks if the object is currently loading.
   *
   * @returns True if loading is in progress, false otherwise
   */
  isLoading(): boolean;
}

/**
 * Type guard to check if an object implements IAsyncLoadable.
 *
 * @param obj - The object to check
 * @returns True if the object implements IAsyncLoadable
 */
export function isAsyncLoadable(obj: any): obj is IAsyncLoadable {
  return obj && obj[IAsyncLoadableTag] === true;
}

